import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Card from '@mui/joy/Card';
import Typography from '@mui/joy/Typography';
import LinearProgress from '@mui/joy/LinearProgress';
import Alert from '@mui/joy/Alert';
import IconButton from '@mui/joy/IconButton';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemContent from '@mui/joy/ListItemContent';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import Autocomplete from '@mui/joy/Autocomplete';
import AutocompleteOption from '@mui/joy/AutocompleteOption';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import { useQuery } from '@tanstack/react-query';
import { CloudArrowUp as CloudUpload, FileText as Description, Camera as CameraAlt, CheckCircle, XCircle as Error, X as Close, Image, PaperPlaneTilt as Send } from '@phosphor-icons/react';
import { api } from '../../../lib/api';
import type { PolicyAnalysis } from '../types';

interface PolicyContact {
  id: number;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

// ContactDetailSerializer has no full_name — only the list one does — so a
// preset contact fetched by id would otherwise show as a blank locked field.
const contactLabel = (contact: PolicyContact) =>
  contact.full_name ||
  [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
  `Contact ${contact.id}`;

interface PolicyUploaderProps {
  onAnalysisComplete: (analysis: PolicyAnalysis) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  /** Customer the analysis belongs to when opened from a contact or job. */
  presetContactId?: number | null;
  /** The route already answered "whose policy is this?" — don't let it drift. */
  contactLocked?: boolean;
}

const PolicyUploader: React.FC<PolicyUploaderProps> = ({
  onAnalysisComplete,
  loading,
  setLoading,
  presetContactId = null,
  contactLocked = false,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [contactId, setContactId] = useState<number | null>(presetContactId);
  const [contactSearch, setContactSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The job → contact lookup resolves after first render, so adopt it when it
  // lands — but never over a customer the user has already picked.
  useEffect(() => {
    if (presetContactId) {
      setContactId(prev => prev ?? presetContactId);
    }
  }, [presetContactId]);

  const { data: contacts = [] } = useQuery<PolicyContact[]>({
    queryKey: ['contacts', contactSearch],
    queryFn: async () => {
      const url = contactSearch
        ? `/api/crm/contacts/?search=${encodeURIComponent(contactSearch)}`
        : '/api/crm/contacts/';
      const response = await api.get<PolicyContact[] | { results: PolicyContact[] }>(url);
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    },
    staleTime: 30000,
  });

  // A preset contact usually isn't on the unsearched first page, so fetch it
  // by id to have a name to show next to the lock.
  const { data: presetContact } = useQuery<PolicyContact>({
    queryKey: ['contact', presetContactId],
    queryFn: async () => {
      const response = await api.get<PolicyContact>(`/api/crm/contacts/${presetContactId}/`);
      return response.data;
    },
    enabled: Boolean(presetContactId),
    staleTime: 30000,
  });

  const selectedContact =
    contacts.find(contact => contact.id === contactId) ||
    (presetContact?.id === contactId ? presetContact : null) ||
    null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (files: File[]) => {
    setError(null);
    
    // Validate each file
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];
    const validFiles: File[] = [];
    
    for (const file of files) {
      const fileType = file.type || '';
      const fileName = file.name.toLowerCase();
      
      // Skip PDFs when adding to multiple images
      if (fileName.endsWith('.pdf')) {
        if (selectedFiles.length > 0 || files.length > 1) {
          setError('PDFs must be uploaded separately');
          continue;
        }
      }
      
      if (!allowedTypes.includes(fileType) && 
          !fileName.endsWith('.pdf') && 
          !fileName.endsWith('.jpg') && 
          !fileName.endsWith('.jpeg') && 
          !fileName.endsWith('.png') && 
          !fileName.endsWith('.heic')) {
        setError('Please upload PDF or image files (JPEG, PNG, HEIC)');
        continue;
      }

      if (file.size > 30 * 1024 * 1024) {
        setError(`${file.name} exceeds 30MB limit`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setError(null);
  };

  const handleAnalyze = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setError(null);
    setStatus('Uploading documents...');
    setProgress(20);
    setLoading(true);

    const formData = new FormData();
    
    // Check if it's a single PDF or multiple images
    const isPDF = selectedFiles.length === 1 && selectedFiles[0].name.toLowerCase().endsWith('.pdf');
    
    if (isPDF) {
      formData.append('file', selectedFiles[0]);
    } else {
      // Multiple images
      selectedFiles.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });
      formData.append('file_count', selectedFiles.length.toString());
    }

    // Without this the analysis lands unattached and is invisible from the
    // customer's record — the backend has always accepted it.
    if (contactId) {
      formData.append('contact', contactId.toString());
    }

    try {
      setStatus('Analyzing policy documents...');
      setProgress(60);

      const response = await api.post<PolicyAnalysis>('/api/policy-copilot/analyses/', formData);

      setProgress(80);

      setProgress(100);
      setStatus('Upload saved. Extraction will appear in the review panel.');
      onAnalysisComplete(response.data);
      setSelectedFiles([]);
    } catch (err: unknown) {
      console.error('Upload error:', err);
      // ApiClient hangs the parsed error body on `responseData`; reading an
      // axios-shaped `response.data` swallowed every message the API sent,
      // including "Image 2: ..." from multi-image validation.
      const detail = (err as { responseData?: { detail?: string } })?.responseData?.detail;
      setError(detail || 'Failed to analyze documents');
      setStatus('');
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };


  const handleButtonClick = () => fileInputRef.current?.click();

  return (
    <Card>
      <Typography level="h3" sx={{ mb: 2 }}>
        Upload Policy Document
      </Typography>

      <FormControl sx={{ mb: 2 }}>
        <FormLabel>Customer</FormLabel>
        <Autocomplete
          placeholder="Search for a contact"
          value={selectedContact}
          inputValue={contactLocked ? (selectedContact ? contactLabel(selectedContact) : '') : contactSearch}
          onInputChange={(_, value) => setContactSearch(value)}
          onChange={(_, value) => setContactId(value?.id ?? null)}
          options={contacts}
          getOptionLabel={(option) => contactLabel(option)}
          isOptionEqualToValue={(option, selected) => option.id === selected?.id}
          disabled={loading || contactLocked}
          renderOption={(props, option) => (
            <AutocompleteOption {...props} key={option.id}>
              <div>
                <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                  {contactLabel(option)}
                </Typography>
                {option.email && (
                  <Typography level="body-xs" textColor="text.secondary">
                    {option.email}
                  </Typography>
                )}
              </div>
            </AutocompleteOption>
          )}
        />
        <FormHelperText>
          {contactLocked
            ? 'Taken from the record you opened Policy Copilot from'
            : 'Optional — links the analysis to the customer’s record'}
        </FormHelperText>
      </FormControl>

      <Box
        sx={{
          border: dragActive ? '2px dashed' : '2px dashed',
          borderColor: dragActive ? 'primary.500' : 'neutral.300',
          borderRadius: 'md',
          p: { xs: 2, md: 3 },
          textAlign: 'center',
          backgroundColor: dragActive ? 'primary.softBg' : 'background.level1',
          transition: 'all 0.2s',
          cursor: 'pointer',
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.heic,image/*"
          onChange={handleChange}
          multiple
          style={{ display: 'none' }}
        />

        <CloudUpload size={48} color="var(--joy-palette-primary-500)" style={{ marginBottom: 16 }} />
        
        <Typography level="h4" sx={{ mb: 1 }}>
          Drop your policy documents here
        </Typography>
        
        <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
          or click to browse files
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Description />}
            disabled={loading}
          >
            Select PDF
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<CameraAlt />}
            disabled={loading}
          >
            Select Images
          </Button>
        </Box>

        <Typography level="body-sm" sx={{ mt: 2, color: 'text.secondary' }}>
          Supported formats: PDF, JPEG, PNG, HEIC • Up to 25 pages/images and 30MB combined
        </Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          For multiple page policies, upload all images together
        </Typography>
      </Box>

      {/* Show selected files */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Selected Files ({selectedFiles.length})
          </Typography>
          <List size="sm">
            {selectedFiles.map((file, index) => (
              <ListItem key={index}>
                <ListItemDecorator>
                  {file.name.toLowerCase().endsWith('.pdf') ? <Description /> : <Image />}
                </ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-sm">{file.name}</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </ListItemContent>
                <IconButton aria-label="Remove selected file"
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={() => removeFile(index)}
                  disabled={loading}
                >
                  <Close />
                </IconButton>
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="solid"
              color="primary"
              size="lg"
              startDecorator={<Send />}
              onClick={handleAnalyze}
              disabled={loading || selectedFiles.length === 0}
              sx={{ flex: 1 }}
            >
              Analyze Now
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setSelectedFiles([]);
                setError(null);
              }}
              disabled={loading}
            >
              Clear All
            </Button>
          </Box>
        </Box>
      )}

      {loading && (
        <Box sx={{ mt: 3 }}>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            {status}
          </Typography>
          <LinearProgress determinate value={progress} />
        </Box>
      )}

      {error && (
        <Alert color="danger" sx={{ mt: 2 }} startDecorator={<Error />}>
          {error}
        </Alert>
      )}

      {status === 'Analysis complete!' && (
        <Alert color="success" sx={{ mt: 2 }} startDecorator={<CheckCircle />}>
          {status}
        </Alert>
      )}

      <Alert color="neutral" sx={{ mt: 3 }}>
        <Typography level="body-sm">
          <strong>Source review:</strong> Original documents are retained with this analysis.
          Text is extracted in ApexOS. Optional AI suggestions require review against the
          source before any values are saved to a job.
        </Typography>
      </Alert>
    </Card>
  );
};

export default PolicyUploader;
